import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Droplets, LayoutDashboard, Users, Gauge, Calculator, Wrench,
  LogOut, Moon, Sun, PanelLeftClose, PanelLeft,
  Droplet, ClipboardList, UserCog, ShieldCheck, Wallet, AlertTriangle,
  Siren, Map as MapIcon, BarChart3, BookOpen, Droplet as DropletIcon,
  Factory, Coins,
} from 'lucide-react';
import { useAuth, usePermissions } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { cn, initials } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Users;
  permission?: string;
}

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Обзор',
    items: [
      { to: '/dashboard', label: 'Дашборд', icon: LayoutDashboard },
      { to: '/reports', label: 'Отчётность', icon: BarChart3, permission: 'reports.read' },
    ],
  },
  {
    group: 'Производство',
    items: [
      { to: '/source', label: 'Источники', icon: Factory, permission: 'infrastructure.read' },
      { to: '/reports/balance', label: 'Водный баланс', icon: DropletIcon, permission: 'reports.read' },
      { to: '/reports/costs', label: 'Затраты и себестоимость', icon: Coins, permission: 'reports.read' },
    ],
  },
  {
    group: 'Абоненты',
    items: [
      { to: '/subscribers', label: 'Лицевые счета', icon: Users, permission: 'subscribers.read' },
      { to: '/meters', label: 'Приборы учёта', icon: Gauge, permission: 'meters.read' },
    ],
  },
  {
    group: 'Показания',
    items: [
      { to: '/readings/entry', label: 'Ввод по маршруту', icon: ClipboardList, permission: 'readings.write' },
      { to: '/readings', label: 'Журнал показаний', icon: Droplet, permission: 'readings.read' },
    ],
  },
  {
    group: 'Расчёты',
    items: [
      { to: '/billing', label: 'Прогоны биллинга', icon: Calculator, permission: 'billing.read' },
    ],
  },
  {
    group: 'Финансы',
    items: [
      { to: '/cash', label: 'Касса', icon: Wallet, permission: 'payments.read' },
      { to: '/debt', label: 'Задолженность', icon: AlertTriangle, permission: 'debt.read' },
    ],
  },
  {
    group: 'Диспетчерская',
    items: [
      { to: '/dispatch', label: 'Журнал заявок', icon: Siren, permission: 'dispatch.read' },
      { to: '/infrastructure/map', label: 'Карта сетей', icon: MapIcon, permission: 'infrastructure.read' },
    ],
  },
  {
    group: 'Справочники',
    items: [
      { to: '/reference', label: 'Все справочники', icon: BookOpen },
      { to: '/tariffs', label: 'Тарифы', icon: Wrench, permission: 'tariffs.read' },
    ],
  },
  {
    group: 'Администрирование',
    items: [
      { to: '/admin/users', label: 'Пользователи', icon: UserCog, permission: 'admin.users.read' },
      { to: '/admin/roles', label: 'Роли и права', icon: ShieldCheck, permission: 'admin.read' },
    ],
  },
];

export function AppShell(): React.JSX.Element {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { theme, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-full bg-canvas text-fg">
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-150',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <Droplets className="size-5 shrink-0 text-accent" aria-hidden />
          {!collapsed && <span className="truncate font-semibold tracking-tight">WaterFlow</span>}
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          {NAV.map((group) => {
            const visible = group.items.filter((i) => !i.permission || can(i.permission));
            if (visible.length === 0) return null;
            return (
              <div key={group.group} className="mb-4">
                {!collapsed && (
                  <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-fg-subtle">
                    {group.group}
                  </div>
                )}
                <ul className="space-y-0.5">
                  {visible.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        // end обязателен: без него /readings подсвечивается
                        // одновременно с /readings/entry как его префикс
                        end
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                            isActive
                              ? 'bg-accent-soft font-medium text-accent'
                              : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                          )
                        }
                      >
                        <item.icon className="size-4 shrink-0" aria-hidden />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? <PanelLeft className="size-4" /> : <PanelLeftClose className="size-4" />}
            {!collapsed && <span>Свернуть</span>}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-end gap-2 border-b border-border bg-surface px-4">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Переключить тему"
            onClick={toggle}
          >
            {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </Button>

          <div className="flex items-center gap-2 border-l border-border pl-3">
            <div className="flex size-8 items-center justify-center rounded-full bg-accent-soft text-xs font-medium text-accent">
              {initials(user?.fullName ?? '?')}
            </div>
            <div className="hidden leading-tight sm:block">
              <div className="text-sm font-medium">{user?.fullName}</div>
              <div className="text-xs text-fg-subtle">{user?.position || user?.email}</div>
            </div>
            <Button variant="ghost" size="sm" aria-label="Выйти" onClick={() => void handleLogout()}>
              <LogOut className="size-4" />
            </Button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
